// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import {
  useAllStatusArtifacts,
  ALL_STATUSES,
  fanoutItems,
  fanoutNotice,
  fetchStatusPages,
} from './allStatusArtifacts';
import type { ArtifactSummary } from '../types/procedures';

export interface ProcedureLibraryFilters {
  q?: string;
  artifactType?: string | null;
  status?: string | null;
  /** Facet selections — applied client-side (OR within a facet, AND across facets). */
  domains?: string[];
  equipmentTypes?: string[];
  riskTiers?: string[];
}

export interface FacetOption {
  value: string;
  count: number;
}

export interface ProcedureLibraryView {
  /** Every artifact matching q/artifactType/status/facets — caller sorts/paginates. */
  items: ArtifactSummary[];
  /** Counts by facet value, over q/artifactType/status matches (not narrowed by facets
   *  themselves — selecting "cooling" shouldn't zero out the other domain options). */
  domainFacets: FacetOption[];
  equipmentFacets: FacetOption[];
  riskFacets: FacetOption[];
  isLoading: boolean;
  error: string | null;
  /** Set when some statuses failed or were capped: `items` is a partial view. */
  notice: string | null;
  /** Refetches the catalog; set only when some statuses failed (a cap won't change on retry). */
  retry: (() => void) | null;
}

function countBy(items: ArtifactSummary[], pick: (_item: ArtifactSummary) => string | undefined) {
  const counts = new Map<string, number>();
  for (const item of items) {
    const value = pick(item);
    if (!value) continue;
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

function matchesFacets(item: ArtifactSummary, filters: ProcedureLibraryFilters): boolean {
  const { domains = [], equipmentTypes = [], riskTiers = [] } = filters;
  if (domains.length && !domains.includes(item.categories?.domain ?? '')) return false;
  if (equipmentTypes.length && !equipmentTypes.includes(item.categories?.equipment_type ?? '')) {
    return false;
  }
  if (riskTiers.length && !riskTiers.includes(item.categories?.risk_tier ?? '')) return false;
  return true;
}

/**
 * "All statuses, no search/type filter" is the same request Home's
 * `useProcedures` makes — routed through `useAllStatusArtifacts` so the two
 * screens share one cached fan-out instead of each re-fetching all 4
 * statuses under their own key. A search term, type, or specific status gets
 * its own scoped fan-out. `domains`/`equipmentTypes`/`riskTiers` never affect
 * which query runs — the API takes one value per facet, not a set, so those
 * are applied (and counted) client-side over whichever page arrived.
 */
export function useProcedureLibrary(filters: ProcedureLibraryFilters): ProcedureLibraryView {
  const { q, artifactType, status } = filters;
  const hasServerFilter = Boolean(q) || Boolean(artifactType) || Boolean(status);

  const sharedQuery = useAllStatusArtifacts({ enabled: !hasServerFilter });

  const scopedQuery = useQuery({
    queryKey: ['admin-center', 'procedure-library', { q, artifactType, status }],
    enabled: hasServerFilter,
    queryFn: () =>
      fetchStatusPages(status ? [status] : ALL_STATUSES, {
        q: q || undefined,
        artifact_type: artifactType ?? undefined,
      }),
  });

  const active = hasServerFilter ? scopedQuery : sharedQuery;
  const isLoading = active.isPending;
  const isError = active.isError;
  const rawItems = fanoutItems(active.data);

  return {
    items: rawItems.filter(item => matchesFacets(item, filters)),
    domainFacets: countBy(rawItems, item => item.categories?.domain),
    equipmentFacets: countBy(rawItems, item => item.categories?.equipment_type),
    riskFacets: countBy(rawItems, item => item.categories?.risk_tier),
    isLoading,
    error: isError && !active.data ? 'Unable to load the procedure library.' : null,
    notice: fanoutNotice(active.data),
    retry: active.data?.failedStatuses.length ? () => void active.refetch() : null,
  };
}
